class Wishlist {
  constructor() {
    this.isWishlistPage = false
    this.storageKey = 'm-wishlist-products'
    this.products = []
    this.productNodes = Object.create(null)
    this.activeRequests = 0
    this.requestQueue = []

    this.pageTemplate = 'page.wishlist'
    this.addedClass = 'added-to-wishlist'
    this.hasItemClass = 'wishlist-has-item'
    this.selectors = {
      container: '.m-wishlist-page-content__wrapper',
      noProducts: '.m-wishlist-no-products',
      wrapper: '.m-wishlist-card',
      productCard: '.kp-wishlist-card',
      wishlistButton: '.m-wishlist-button',
      wishlistText: '.m-wishlist-button-text',
      removeButton: '.m-wishlist-remove-button',
      count: '.m-wishlist-count',
      pageCount: '[data-wishlist-page-count]',
      status: '[data-wishlist-status]',
      retryButton: '.kp-wishlist-retry-button'
    }
    // Some in-app/private browsers deny storage or expose it as null.
    // Keep the current page usable even when persistence is unavailable.
    try {
      const saved = (window.localStorage && JSON.parse(window.localStorage.getItem(this.storageKey) || '[]')) || []
      if (Array.isArray(saved)) this.products = [...new Set(saved.filter(value => typeof value === 'string'))]
    } catch (_) {
      this.products = []
    }
    this.isWishlistPage = MinimogSettings.template === this.pageTemplate
    if (this.isWishlistPage) this.restoreFromLink()
    this.ready = this.init()
  }

  init = async () => {
    this.setWishlistButtonsState()
    this.addEventToWishlistButtons()
    this.updateWishlistCount()
    if (this.isWishlistPage) {
      this.addEventToRemoveButtons()
      this.addEventToRetryButtons()
      await this.renderWishlistPage()
    }
  }

  // /pages/wishlist?items=handle-a,handle-b rebuilds a list on another device (wishlist emails, 9 Oct 2026).
  restoreFromLink = () => {
    let params
    try { params = new URLSearchParams(window.location.search) } catch (_) { return }
    const raw = params.get('items')
    if (!raw) return
    const handles = raw.split(',').map(value => value.trim().toLowerCase()).filter(value => /^[a-z0-9][a-z0-9-]{0,254}$/.test(value)).slice(0, 50)
    if (handles.length) {
      this.products = [...new Set([...this.products, ...handles])]
      this.saveToStorage()
    }
    try {
      params.delete('items')
      const query = params.toString()
      window.history.replaceState(window.history.state, '', window.location.pathname + (query ? '?' + query : '') + window.location.hash)
    } catch (_) {}
  }

  // Confirms a save without leaving the page. The panel script loads only after the first heart tap.
  openSavedPanel = (handle) => {
    if (this.isWishlistPage || !handle) return
    const products = this.products.slice()
    if (window.KPWishlistPanel) return window.KPWishlistPanel.open(handle, products)
    this.pendingPanel = handle
    if (this.panelLoading) return
    const src = window.MinimogThemeScripts && window.MinimogThemeScripts.wishlistPanel
    if (!src) return
    this.panelLoading = true
    const script = document.createElement('script')
    script.src = src
    script.async = true
    script.onload = () => {
      this.panelLoading = false
      if (window.KPWishlistPanel && this.pendingPanel) window.KPWishlistPanel.open(this.pendingPanel, this.products.slice())
    }
    script.onerror = () => { this.panelLoading = false }
    document.head.appendChild(script)
  }

  saveToStorage = () => {
    this.products = Array.from(new Set(this.products))
    try {
      if (window.localStorage) window.localStorage.setItem(this.storageKey, JSON.stringify(this.products))
    } catch (_) {
      // The selection remains available in memory for this page.
    }
  }

  addToWishlist(handle) {
    if (handle && this.products.indexOf(handle) === -1) {
      this.products.push(handle)
      this.saveToStorage()
    }
  }

  removeFromWishlist(handle) {
    this.products = this.products.filter(hdl => hdl !== handle)
    this.saveToStorage()
  }

  setWishlistButtonsState = () => {
    const buttons = document.querySelectorAll(this.selectors.wishlistButton)
    buttons.forEach(btn => {
      const prodHandle = btn && btn.dataset.productHandle
      if (!prodHandle) return
      if (btn.tagName !== 'BUTTON' && btn.tagName !== 'A') {
        btn.setAttribute('role', 'button')
        btn.tabIndex = 0
        if (!btn.dataset.kpKeyboard) {
          btn.dataset.kpKeyboard = 'true'
          btn.addEventListener('keydown', e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); btn.click() } })
        }
      }
      this.updateButtonAccessibility(btn, this.products.includes(prodHandle))
      if (this.products.indexOf(prodHandle) >= 0 && btn && !btn.classList.contains(this.addedClass)) {
        this.toggleButtonState(btn, true)

        if (this.isWishlistPage) {
          btn.classList.remove(this.selectors.wishlistButton.replace('.', ''))
          btn.classList.add(this.selectors.removeButton.replace('.', ''))
        }
      }
    })
  }

  updateWishlistCount = () => {
    const size = this.products.length
    const countElems = document.querySelectorAll(this.selectors.count);
    [...countElems].forEach(elem => {
      elem.textContent = size
      if (size < 1) {
        elem.classList.add('m:hidden')
      } else {
        elem.classList.remove('m:hidden')
      }
    })
    const method = size ? 'add' : 'remove'
    document.body.classList[method](this.hasItemClass)
    if (this.isWishlistPage) {
      const pageCount = document.querySelector(this.selectors.pageCount)
      if (pageCount) pageCount.textContent = `${size} ${size === 1 ? 'item' : 'items'} saved`
    }
  }

  addEventToWishlistButtons = () => {
    addEventDelegate({
      selector: this.selectors.wishlistButton,
      handler: (e, btn) => {
        e.preventDefault()
        const productHandle = btn && btn.dataset.productHandle
        if (productHandle) {
          const active = !btn.classList.contains(this.addedClass)
          this.toggleButtonState(btn, active)
          this.updateWishlistCount()
          if (active) this.openSavedPanel(productHandle)

          document.querySelectorAll(this.selectors.wishlistButton).forEach(btnItem => {
            if (btnItem && btnItem.dataset.productHandle === productHandle && btnItem !== btn) {
              const isAdded = active
              this.toggleButtonState(btnItem, isAdded)
            }
          })
        }
      }
    })
  }

  toggleButtonState = (btn, active) => {
    const productHandle = btn && btn.dataset.productHandle
    const wishlistText = btn && btn.querySelector(this.selectors.wishlistText)

    if (active) {
      this.addToWishlist(productHandle)
      btn.classList.add(this.addedClass)
    } else {
      this.removeFromWishlist(productHandle)
      btn.classList.remove(this.addedClass)
    }

    this.updateButtonAccessibility(btn, active)
    if (wishlistText) wishlistText.textContent = active ? btn.dataset.kpRemoveLabel : btn.dataset.kpAddLabel
  }

  updateButtonAccessibility = (btn, active) => {
    const text = btn.querySelector(this.selectors.wishlistText)
    if (!btn.dataset.kpAddLabel) {
      btn.dataset.kpAddLabel = btn.getAttribute('aria-label') || text?.textContent?.trim() || 'Add to wishlist'
      btn.dataset.kpRemoveLabel = text?.dataset.revertText?.trim() || 'Remove from wishlist'
    }
    btn.setAttribute('aria-label', active ? btn.dataset.kpRemoveLabel : btn.dataset.kpAddLabel)
    btn.setAttribute('aria-pressed', String(active))
  }

  addEventToRemoveButtons = () => {
    addEventDelegate({
      selector: this.selectors.removeButton,
      handler: (e, btn) => {
        e.preventDefault()
        const productHandle = btn && btn.dataset.productHandle
        if (!productHandle) return
        const item = btn.closest(this.selectors.wrapper)
        const nextItem = item && (item.nextElementSibling || item.previousElementSibling)
        this.removeFromWishlist(productHandle)
        delete this.productNodes[productHandle]
        if (item) item.remove()
        this.updateWishlistCount()
        if (!this.products.length) {
          this.showNoProductsMessage()
          document.querySelector('.kp-wishlist__browse')?.focus()
        } else {
          nextItem?.querySelector('.kp-wishlist-card__title a, button')?.focus()
        }
        this.announce('Removed from your favourites.')
      }
    })
  }

  addEventToRetryButtons = () => {
    addEventDelegate({
      selector: this.selectors.retryButton,
      handler: async (e, btn) => {
        e.preventDefault()
        const handle = btn.dataset.productHandle
        const item = btn.closest(this.selectors.wrapper)
        if (!item || item.getAttribute('aria-busy') === 'true') return
        await this.loadWishlistItem(handle, item, true)
        item.querySelector('.kp-wishlist-card__title a, button')?.focus()
      }
    })
  }

  announce(message) {
    const status = document.querySelector(this.selectors.status)
    if (status) status.textContent = message
  }

  wishlistRemoveButton(handle) {
    const button = document.createElement('button')
    button.type = 'button'
    button.classList.add('m-wishlist-remove-button', 'kp-wishlist-card__remove')
    button.dataset.productHandle = handle
    button.setAttribute('aria-label', 'Remove this item from wishlist')
    button.textContent = 'Remove'
    return button
  }

  showItemFailure(handle, item, unavailable) {
    const content = document.createElement('div')
    content.className = 'kp-wishlist__error'
    const message = document.createElement('p')
    message.textContent = unavailable
      ? 'This favourite is no longer available here.'
      : 'This favourite could not load. Please try again.'
    content.appendChild(message)
    if (!unavailable) {
      const retry = document.createElement('button')
      retry.type = 'button'
      retry.className = 'kp-wishlist-retry-button'
      retry.dataset.productHandle = handle
      retry.textContent = 'Try again'
      content.appendChild(retry)
    }
    content.appendChild(this.wishlistRemoveButton(handle))
    item.replaceChildren(content)
    item.dataset.state = unavailable ? 'unavailable' : 'error'
  }

  acquireRequestSlot() {
    return new Promise(resolve => {
      this.requestQueue.push(resolve)
      this.drainRequestQueue()
    })
  }

  drainRequestQueue() {
    while (this.activeRequests < 4 && this.requestQueue.length) {
      this.activeRequests++
      this.requestQueue.shift()()
    }
  }

  loadWishlistItem = async (handle, item, retry = false) => {
    item.setAttribute('aria-busy', 'true')
    if (retry) this.announce('Loading this favourite again.')
    await this.acquireRequestSlot()
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 15000)
    try {
      // Use a dedicated small HTML view. Failed responses are never cached, so
      // a shopper can retry an individual item without reloading the whole list.
      const url = formatUrl('products', encodeURIComponent(handle), 'view=kp-wishlist-card')
      const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal })
      if (response.status === 404 || response.status === 410) {
        if (this.products.includes(handle)) this.showItemFailure(handle, item, true)
        return
      }
      if (!response.ok) throw new Error('Wishlist product request failed')
      const html = await response.text()
      if (!this.products.includes(handle)) return
      const template = document.createElement('template')
      template.innerHTML = html
      const card = template.content.querySelector(this.selectors.productCard)
      if (!card) {
        if (template.content.querySelector('[data-wishlist-unavailable]')) {
          this.showItemFailure(handle, item, true)
          return
        }
        throw new Error('Wishlist product card missing')
      }
      // The first visible card should not wait for lazy-image scheduling after
      // its HTML request; remaining cards keep native image lazy loading.
      if (item.dataset.priority === 'true') {
        const image = card.querySelector('img')
        if (image) {
          image.loading = 'eager'
          image.fetchPriority = 'high'
        }
      }
      item.replaceChildren(card)
      item.dataset.state = 'ready'
      this.productNodes[handle] = item
      if (retry) this.announce('Your favourite is ready.')
    } catch (_) {
      if (this.products.includes(handle)) {
        this.showItemFailure(handle, item, false)
        if (retry) this.announce('This favourite could not load. You can try again.')
      }
    } finally {
      clearTimeout(timeout)
      item.setAttribute('aria-busy', 'false')
      this.activeRequests--
      this.drainRequestQueue()
    }
  }

  renderWishlistPage = () => {
    if (this.rendering) return this.rendering
    this.rendering = this.renderWishlistItems().finally(() => { this.rendering = null })
    return this.rendering
  }

  renderWishlistItems = async () => {
    const container = document.querySelector(this.selectors.container)
    if (!container) return
    if (!this.products.length) {
      this.showNoProductsMessage()
      return
    }
    container.hidden = false
    container.classList.remove('m:hidden')
    container.setAttribute('aria-busy', 'true')
    const noProducts = document.querySelector(this.selectors.noProducts)
    if (noProducts) {
      noProducts.hidden = true
      noProducts.classList.add('m:hidden')
    }
    const handles = this.products.slice()
    const items = handles.map((handle, index) => {
      if (this.productNodes[handle]?.dataset.state === 'ready') return this.productNodes[handle]
      const item = document.createElement('li')
      item.classList.add('m-wishlist-card', 'kp-wishlist__slot')
      item.dataset.productHandle = handle
      item.dataset.priority = String(index === 0)
      item.setAttribute('aria-busy', 'true')
      item.setAttribute('aria-label', 'Loading saved product')
      item.innerHTML = '<span class="kp-wishlist__loading-image" aria-hidden="true"></span><span class="kp-wishlist__loading-line" aria-hidden="true"></span><span class="kp-wishlist__loading-line" aria-hidden="true"></span>'
      return item
    })
    container.replaceChildren(...items)
    // Reserve each position, then reveal each response immediately. Four small
    // workers bound request volume without letting one slow item hide the rest.
    let cursor = 0
    const worker = async () => {
      while (cursor < handles.length) {
        const index = cursor++
        if (this.products.includes(handles[index]) && items[index].dataset.state !== 'ready') {
          await this.loadWishlistItem(handles[index], items[index])
        }
        items[index].removeAttribute('aria-label')
      }
    }
    await Promise.all(Array.from({ length: Math.min(4, handles.length) }, worker))
    container.setAttribute('aria-busy', 'false')
    const failed = container.querySelectorAll('[data-state="error"]').length
    this.announce(failed ? 'Some favourites could not load. Try again on the affected items.' : 'Your favourites are ready.')
  }

  showNoProductsMessage = () => {
    const container = document.querySelector(this.selectors.container)
    const noProducts = document.querySelector(this.selectors.noProducts)
    if (container) {
      container.hidden = true
      container.classList.add('m:hidden')
      container.setAttribute('aria-busy', 'false')
    }
    if (noProducts) {
      noProducts.hidden = false
      noProducts.classList.remove('m:hidden')
    }
  }
}

MinimogTheme.Wishlist = new Wishlist()
